import { Suspense, useEffect, useState } from "react";
import { page, preloadPages } from "@/lib/lazyPage";
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
import OpenSiteMenu from "@/components/OpenSiteMenu";
import RefreshButton from "@/components/RefreshButton";
import { prefetchData } from "@/lib/prefetch";
import OutboxBadge from "@/components/OutboxBadge";
import PullToRefresh from "@/components/PullToRefresh";
const SideBySide = page(() => import("@/pages/SideBySide"));
const BatchUpload = page(() => import("@/pages/batch/BatchUpload"));
import BatchUploadLink from "@/pages/batch/BatchUploadLink";
import SendToVaCard from "@/pages/team/SendToVaCard";
import { Toaster } from "@/components/ui/sonner";
import { Settings, Zap, LogOut, Users, UsersRound, Gift, Wallet, Bell, HelpCircle, MonitorDown, Lock } from "lucide-react";
import DraftCreator from "@/pages/DraftCreator";
const DraftLibrary = page(() => import("@/pages/DraftLibrary"));
const Research = page(() => import("@/pages/Research"));
const MediaBatches = page(() => import("@/pages/media/MediaBatches"));
const DashboardGate = page(() => import("@/pages/DashboardGate"));
const PlanBilling = page(() => import("@/pages/PlanBilling"));
import { useUsage } from "@/lib/usage";
const AdminStats = page(() => import("@/pages/AdminStats"));
const ExtensionConnect = page(() => import("@/pages/ExtensionConnect"));
import PlanBadge from "@/pages/PlanBadge";
import UpgradeDialog from "@/pages/UpgradeDialog";
import TrialReminderDialog from "@/pages/TrialReminderDialog";
import FirstSaleDialog from "@/pages/FirstSaleDialog";
import OnboardingChecklist from "@/pages/OnboardingChecklist";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import EbaySettings from "@/pages/EbaySettings";
import UseModeChooser from "@/pages/UseModeChooser";
import VaTour, { openVaTour } from "@/pages/team/VaTour";
import PcInstallGuide, { canOfferInstall, openPcInstall } from "@/pages/team/PcInstallGuide";
import WhatnotSettings from "@/pages/WhatnotSettings";
import InstallHint from "@/pages/InstallHint";
import UpdateBar from "@/pages/UpdateBar";
import UsageChip from "@/pages/UsageChip";
import UsageNearLimitBanner from "@/pages/UsageNearLimitBanner";
import Login from "@/pages/Login";
import LegalPage, { LEGAL_PATHS, LegalLinks } from "@/pages/LegalPage";
import AuthCallback from "@/pages/AuthCallback";
import { UsageProvider } from "@/lib/usage";
import { AuthProvider, useAuth } from "@/lib/auth";
import { useTeam } from "@/lib/team";
const TeamPage = page(() => import("@/pages/TeamPage"));
const MyEarnings = page(() => import("@/pages/team/MyEarnings"));
import PaydayReminder from "@/pages/team/PaydayReminder";
import OwnerQuestions from "@/pages/team/OwnerQuestions";
import LockedPage, { LockedTab, lockFor } from "@/pages/LockedPage";
import InboxPage, { InboxBanner, InboxHeaderButton } from "@/pages/team/Inbox";
import ScanFlash from "@/pages/ScanFlash";
import { InboxProvider } from "@/lib/inbox";
import AppSetupCard from "@/pages/lister/AppSetupCard";
const RareFinds = page(() => import("@/pages/rare/RareFinds"));
const PayoutGallery = page(() => import("@/pages/rare/PayoutGallery"));
const AlertSettings = page(() => import("@/pages/AlertSettings"));
import SaleCheer from "@/pages/SaleCheer";
const ListerQueue = page(() => import("@/pages/ListerQueue"));
const PayStatement = page(() => import("@/pages/team/PayStatement"));
import AssistantBanner from "@/components/AssistantBanner";
import BatchDoneAlert from "@/pages/batch/BatchDoneAlert";
import AtmCelebration from "@/pages/AtmCelebration";
import OverdueNudge from "@/pages/batch/OverdueNudge";
const InvitePage = page(() => import("@/pages/InvitePage"));
const MyListings = page(() => import("@/pages/MyListings"));
import { captureRefFromUrl, captureSource, ReferralClaimer } from "@/lib/referral";
import "@/App.css";
import PacManBar from "@/pages/worth/PacManBar";
import Edgar from "@/edgar/Edgar";
import AssistantWelcome from "@/components/AssistantWelcome";
const ToolsPage = page(() => import("@/pages/ToolsPage"));
const InventoryPage = page(() => import("@/pages/inventory/InventoryPage"));
const LocationsPage = page(() => import("@/pages/LocationsPage"));
const PickListPage = page(() => import("@/pages/inventory/PickListPage"));
const WholesalePage = page(() => import("@/pages/wholesale/WholesalePage"));
const InvoiceEditor = page(() => import("@/pages/wholesale/InvoiceEditor"));

captureSource();
captureRefFromUrl();

function LogoutButton() {
  const { user, logout } = useAuth();
  if (!user) return null;
  return (
    <button
      type="button"
      onClick={logout}
      className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-3 text-xs font-medium max-sm:w-9 max-sm:gap-0 max-sm:px-0 text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900"
      aria-label={`Sign out ${user?.name || user?.email || ""}`}
      title={`Signed in as ${user?.email || ""} — tap to sign out`}
      data-testid="logout-btn"
    >
      {user?.picture ? (
        <img src={user.picture} alt="" className="h-5 w-5 rounded-full" referrerPolicy="no-referrer" />
      ) : null}
      <LogOut className={`h-3.5 w-3.5 ${user?.picture ? "max-sm:hidden" : ""}`} />
    </button>
  );
}

// Assistants get two tabs at most: their work and where it comes from.
function AssistantTabs({ team, current }) {
  const tabs = [
    team.canPhotos && ["/batch", "batch", "Upload", "nav-batches"],
    team.canList && !team.hideQueue && ["/queue", "queue", "Queue", "nav-queue"],
    team.role === "photos" && ["/library", "library", "Drafts", "nav-library"],
    (team.role === "lister" || (team.hideQueue && !team.canPhotos)) && ["/batch", "batch", "Batches", "nav-batches"],
    ["/locations", "locations", "Locations", "nav-locations"],
    ["/pick-list", "pick", "Pick list", "nav-pick-list"],
  ].filter(Boolean).filter((t, i, a) => a.findIndex((x) => x[0] === t[0]) === i);
  return tabs.map(([to, k, label, id]) => (
    <Link key={id} to={to} data-testid={id}
      className={`flex-1 rounded-full px-3 py-1.5 text-center transition-colors sm:flex-none ${current === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}>
      {label}
    </Link>
  ));
}

function Nav() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const team = useTeam();
  const current = pathname.startsWith("/locked/")
    ? `locked-${pathname.slice(8)}`
    : pathname.startsWith("/alerts")
    ? "alerts"
    : pathname.startsWith("/media")
    ? "media"
    : pathname.startsWith("/queue")
    ? "queue"
    : pathname.startsWith("/library")
    ? "library"
    : pathname.startsWith("/batch")
      ? "batch"
    : pathname.startsWith("/research")
      ? "research"
      : pathname.startsWith("/dashboard")
        ? "dashboard"
      : pathname.startsWith("/inventory")
        ? "inventory"
        : pathname.startsWith("/locations")
        ? "locations"
        : pathname.startsWith("/pick-list")
        ? "pick"
        : pathname.startsWith("/listings")
          ? "listings"
          : "create";
  return (
    <header className="pt-safe sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70">
      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-x-2 gap-y-2 px-2 min-[360px]:px-3 py-2.5 lg:flex-nowrap sm:px-6 sm:py-3">
        <Link to={team.researchOnly ? "/research" : "/"} className="flex min-w-0 items-center gap-2" data-testid="brand-link">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#0053DA] font-mono text-sm font-bold text-white">SL</div>
          <div className="hidden min-w-0 flex-col leading-tight">
            <span className="truncate text-sm font-semibold tracking-tight text-slate-900">SnapList</span>
            <span className="truncate text-[10px] font-mono uppercase tracking-widest text-slate-500">eBay Draft AI</span>
          </div>
        </Link>
          <div className={`order-last flex w-full items-center gap-0 rounded-full border border-slate-200 bg-slate-50 p-0.5 text-xs font-medium sm:gap-1 sm:p-1 lg:order-none lg:w-auto overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>a]:min-w-0 [&>a]:shrink-0 [&>a]:whitespace-nowrap [&>a]:px-[clamp(2px,0.8vw,6px)] [&>a]:text-[clamp(9px,2.7vw,11px)] sm:[&>a]:flex-1 sm:[&>a]:px-3 sm:[&>a]:text-xs lg:[&>a]:flex-none lg:[&>a]:px-3`} data-testid="nav-tabs">
            {team.isAssistant ? <AssistantTabs team={team} current={current} /> : <>
            {team.researchPlan && <LockedTab feature="create" current={current} />}
            {team.canPhotos && !team.researchOnly && <Link
              to="/"
              data-testid="nav-create"
              className={`flex-1 rounded-full px-2.5 py-1.5 text-center transition-colors sm:flex-none sm:px-3 ${current === "create" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
            >
              Create
            </Link>}
            {team.canPhotos && <Link
              to="/research"
              data-testid="nav-research"
              className={`flex-1 rounded-full px-2.5 py-1.5 text-center transition-colors sm:flex-none sm:px-3 ${current === "research" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
            >
              Research
            </Link>}
            {team.isAssistant && team.canList && !team.hideQueue && <Link
              to="/queue"
              data-testid="nav-queue"
              className={`flex-1 rounded-full px-2.5 py-1.5 text-center transition-colors sm:flex-none sm:px-3 ${current === "queue" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
            >
              Queue
            </Link>}
            {(!team.canPhotos || team.researchOnly) && <Link
              to="/batch"
              data-testid="nav-batches"
              className={`flex-1 rounded-full px-2.5 py-1.5 text-center transition-colors sm:flex-none sm:px-3 ${current === "batch" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
            >
              Batches
            </Link>}
            {team.isAssistant && <Link
              to="/media"
              data-testid="nav-media"
              className={`flex-1 rounded-full px-2.5 py-1.5 text-center transition-colors sm:flex-none sm:px-3 ${current === "media" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
            >
              Media
            </Link>}
            {!team.researchOnly && <Link
              to="/library"
              data-testid="nav-library"
              className={`flex-1 rounded-full px-2.5 py-1.5 text-center transition-colors sm:flex-none sm:px-3 ${current === "library" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
            >
              Library
            </Link>}
            {team.canList && !team.researchOnly && <Link
              to="/listings"
              data-testid="nav-listings"
              className={`flex-1 rounded-full px-2.5 py-1.5 text-center transition-colors sm:flex-none sm:px-3 ${current === "listings" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
            >
              Listings
            </Link>}
            {!team.isAssistant && !team.researchOnly && !team.researchPlan && <Link
              to="/inventory"
              data-testid="nav-inventory"
              className={`flex-1 rounded-full px-2.5 py-1.5 text-center transition-colors sm:flex-none sm:px-3 ${current === "inventory" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
            >
              Inventory
            </Link>}
            {!team.isAssistant && !team.researchOnly && <Link
              to="/dashboard"
              data-testid="nav-dashboard"
              className={`flex-1 rounded-full px-2.5 py-1.5 text-center transition-colors sm:flex-none sm:px-3 ${current === "dashboard" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
            >
              Dashboard
            </Link>}
            {team.researchPlan && <>{["library", "listings", "dashboard"].map((f) => <LockedTab key={f} feature={f} current={current} />)}</>}
            </>}
          </div>
        <nav className="flex min-w-0 shrink-0 items-center gap-0.5 min-[360px]:gap-1 min-[400px]:gap-1.5 sm:gap-2 max-[399px]:[&>a]:!h-8 max-[399px]:[&>a]:!w-8 max-[399px]:[&>button]:!h-8 max-[399px]:[&>button]:!w-8" data-testid="header-actions">
          {team.canList && !team.researchOnly && <OpenSiteMenu />}
          <RefreshButton />
          <Link to="/alerts" data-testid="open-alert-settings" aria-label="Alert settings" title="Alerts"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50">
            <Bell className="h-4 w-4" />
          </Link>
          {team.isAssistant && <InboxHeaderButton />}
          {team.isAssistant && (
            <button type="button" onClick={openVaTour} data-testid="open-va-tour" aria-label="How it works" title="How it works"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50">
              <HelpCircle className="h-4 w-4" />
            </button>
          )}
          {team.isAssistant && canOfferInstall() && (
            <button type="button" onClick={openPcInstall} data-testid="open-pc-install" aria-label="Install the app" title="Install the app"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50">
              <MonitorDown className="h-4 w-4" />
            </button>
          )}
          {team.isAssistant && (
            <Link to="/earnings" data-testid="open-my-earnings" aria-label="My earnings" title="My earnings"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-600 transition-colors hover:bg-emerald-100">
              <Wallet className="h-4 w-4" />
            </Link>
          )}
          {!team.isAssistant && <PlanBadge />}
          {!team.isAssistant && <UsageChip />}
          {!team.isAssistant && !team.researchOnly && (
            <Link to="/team" data-testid="open-team" aria-label="Team" title="Team"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 transition-colors hover:bg-slate-50">
              <UsersRound className="h-4 w-4" />
            </Link>
          )}
          {team.researchPlan && (
            <Link to="/locked/team" data-testid="open-team-locked" aria-label="Team (locked)" title="Team — upgrade to unlock"
              className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 transition-colors hover:bg-slate-50">
              <UsersRound className="h-4 w-4" />
              <Lock className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full bg-slate-900 p-0.5 text-white" />
            </Link>
          )}
          {user?.is_admin ? (
            <Link to="/admin" data-testid="open-admin-stats" aria-label="Subscribers" title="Subscribers"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 transition-colors hover:bg-slate-50">
              <Users className="h-4 w-4" />
            </Link>
          ) : null}
          {user?.is_admin ? <WhatnotSettings
            trigger={
              <button
                type="button"
                data-testid="open-whatnot-settings"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-amber-500 transition-colors hover:bg-amber-50"
                aria-label="Whatnot settings"
                title="Whatnot settings"
              >
                <Zap className="h-4 w-4" />
              </button>
            }
          /> : null}
          {!team.isAssistant && <><Link to="/invite" data-testid="open-invite" aria-label="Invite friends, get free months" title="Invite friends, get free months"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-600 transition-colors hover:bg-emerald-100">
            <Gift className="h-4 w-4" />
          </Link>
          <EbaySettings
            trigger={
              <button
                type="button"
                data-testid="open-ebay-settings"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50 hover:text-[#0053DA]"
                aria-label="eBay settings"
              >
                <Settings className="h-4 w-4" />
              </button>
            }
          /></>}
          <LogoutButton />
        </nav>
      </div>
    </header>
  );
}

/** Switches between loading, login screen, and the real app based on
 *  auth state. The session_id-fragment check runs synchronously during
 *  render so we never risk calling /auth/me before the exchange completes. */
const PLAN_FREE_PATHS = ["/billing", "/invite", "/payment/success", "/payment/cancel", "/admin", "/extension"];

// No free tier: once the trial is over, everything but billing redirects to Plan & Billing.
function PlanRequiredRedirect() {
  const { plan, loading } = useUsage();
  const { pathname } = useLocation();
  if (loading || plan !== "free" || PLAN_FREE_PATHS.includes(pathname)) return null;
  return <Navigate to="/billing" replace />;
}

const ASSISTANT_BLOCKED_PATHS = ["/dashboard", "/billing", "/invite", "/admin", "/team", "/payment/success", "/payment/cancel"];

// Assistants only reach the pages their role covers.
function AssistantRedirect() {
  const { isAssistant, canList, hideQueue } = useTeam();
  const { pathname, search } = useLocation();
  if (!isAssistant) return null;
  const editing = pathname === "/" && new URLSearchParams(search).has("edit");
  const off = ASSISTANT_BLOCKED_PATHS.includes(pathname) || (hideQueue && pathname === "/queue") || (!editing && ["/", "/research", "/listings"].includes(pathname));
  return off ? <Navigate to={canList && !hideQueue ? "/queue" : "/batch"} replace /> : null;
}

const RESEARCH_HIDDEN_PATHS = ["/", "/library", "/listings", "/dashboard", "/queue", "/team", "/extension"];


// Research only mode: drafting and listing pages go to Research.
function ResearchOnlyRedirect() {
  const { researchOnly, researchPlan } = useTeam();
  const { pathname } = useLocation();
  if (researchPlan && lockFor(pathname)) return <Navigate to={`/locked/${lockFor(pathname)}`} replace />;
  return researchOnly && RESEARCH_HIDDEN_PATHS.includes(pathname) ? <Navigate to="/research" replace /> : null;
}

function OwnerOnly({ children }) {
  return useTeam().isAssistant ? null : children;
}

function Gate() {
  const location = useLocation();
  const { isAuthenticated, checkAuth, user } = useAuth();
  useEffect(() => { if (isAuthenticated) preloadPages(); }, [isAuthenticated]);
  useEffect(() => {
    if (!isAuthenticated || !user) return undefined;
    const t = setTimeout(() => prefetchData(user), 1500);
    return () => clearTimeout(t);
  }, [isAuthenticated, user?.user_id, user?.team_role]); // eslint-disable-line react-hooks/exhaustive-deps
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    const on = () => { setRefreshKey((k) => k + 1); checkAuth(); };
    window.addEventListener("snaplist:refresh", on);
    return () => window.removeEventListener("snaplist:refresh", on);
  }, [checkAuth]);

  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  if (LEGAL_PATHS.includes(location.pathname)) return <LegalPage doc={location.pathname.slice(1)} />;
  if (isAuthenticated === null) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="auth-loading">
        <PacManBar className="" testid="auth-loading-bar" />
      </div>
    );
  }
  if (isAuthenticated === false) return <Login />;
  if (location.pathname.startsWith("/side-by-side/")) {
    return <Suspense fallback={null}><Routes><Route path="/side-by-side/:id" element={<SideBySide />} /></Routes></Suspense>;
  }

  return (
    <UsageProvider key={refreshKey}>
      <InboxProvider>
      <ReferralClaimer />
      <Nav />
      <OutboxBadge />
      <PullToRefresh />
      <main className="mx-auto w-full max-w-5xl px-3 py-5 sm:px-6 sm:py-10">
        <AssistantBanner />
        <AssistantWelcome />
        <InboxBanner />
        <BatchDoneAlert />
        <AtmCelebration />
        <ScanFlash />
        <OverdueNudge />
        <OwnerOnly><PaydayReminder /></OwnerOnly>
        <OwnerOnly><OwnerQuestions /></OwnerOnly>
        <AppSetupCard />
        <OwnerOnly><SaleCheer /></OwnerOnly>
        <UsageNearLimitBanner />
        <PlanRequiredRedirect />
        <AssistantRedirect />
        <ResearchOnlyRedirect />
        <VaTour />
        <PcInstallGuide />
        <UseModeChooser />
        <Suspense fallback={<PacManBar className="mx-auto mt-10 max-w-xs" testid="page-loading" />}>
        <Routes>
          <Route path="/" element={<><OwnerOnly><OnboardingChecklist /></OwnerOnly><SendToVaCard /><BatchUploadLink /><DraftCreator /></>} />
          <Route path="/batch" element={<BatchUpload />} />
          <Route path="/research" element={<Research />} />
          <Route path="/media" element={<div className="space-y-4"><h1 className="text-2xl font-bold tracking-tight text-slate-900">Media batches</h1><MediaBatches /></div>} />
          <Route path="/library" element={<DraftLibrary />} />
          {/* Old home-screen shortcuts may still point here; send them to the Library. */}
          <Route path="/whatnot-phone" element={<Navigate to="/library" replace />} />
          <Route path="/whatnot-tablet" element={<Navigate to="/library" replace />} />
          <Route path="/queue" element={<ListerQueue />} />
          <Route path="/alerts" element={<AlertSettings />} />
          <Route path="/inbox" element={<InboxPage />} />
          <Route path="/rare-finds" element={<RareFinds />} />
          <Route path="/payouts" element={<PayoutGallery />} />
          <Route path="/listings" element={<MyListings />} />
          <Route path="/dashboard" element={<DashboardGate />} />
          <Route path="/billing" element={<PlanBilling />} />
          <Route path="/locked/:feature" element={<LockedPage />} />
          <Route path="/invite" element={<InvitePage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/tools" element={<ToolsPage />} />
          <Route path="/inventory" element={<OwnerOnly><InventoryPage /></OwnerOnly>} />
          <Route path="/locations" element={<LocationsPage />} />
          <Route path="/pick-list" element={<PickListPage />} />
          <Route path="/wholesale" element={<WholesalePage />} />
          <Route path="/wholesale/:id" element={<InvoiceEditor />} />
          <Route path="/earnings" element={<MyEarnings />} />
          <Route path="/pay-statement/:id" element={<PayStatement />} />
          <Route path="/admin" element={<AdminStats />} />
          <Route path="/extension" element={<ExtensionConnect />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
        </Routes>
        </Suspense>
      </main>
      <LegalLinks className="pb-8" />
      <InstallHint />
      <UpgradeDialog />
      <TrialReminderDialog />
      <OwnerOnly><FirstSaleDialog /></OwnerOnly>
      <Edgar />
      </InboxProvider>
    </UsageProvider>
  );
}

function App() {
  return (
    <div className="min-h-screen overflow-x-hidden bg-[#F8FAFC] font-sans text-slate-900 antialiased">
      <BrowserRouter>
        <AuthProvider>
          <Gate />
          <UpdateBar />
          <Toaster position="top-center" richColors offset="calc(env(safe-area-inset-top) + 16px)" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
